import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient } from '@/lib/api';

export type NativeToolEntry = {
  value: string;
  label: string;
  description?: string;
  cost_hint?: { eur: number; unit: string } | null;
};

interface NativeToolsPickerProps {
  provider: string;
  model?: string;
  selected: string[];
  onChange: (tools: string[]) => void;
  label?: string;
  description?: string;
  compact?: boolean;
  pruneInvalidOnLoad?: boolean;
}

export function NativeToolsPicker({
  provider,
  model,
  selected,
  onChange,
  label = 'Tool nativi',
  description,
  compact = false,
  pruneInvalidOnLoad = true,
}: NativeToolsPickerProps) {
  const [catalog, setCatalog] = useState<NativeToolEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!provider) {
      setCatalog([]);
      return;
    }
    const ctl = new AbortController();
    setLoading(true);
    const params = new URLSearchParams({ provider });
    if (model) params.set('model', model);
    apiClient
      .get<{ data?: NativeToolEntry[] } | NativeToolEntry[]>(`/ai-tools/native-tools-catalog?${params.toString()}`)
      .then((res) => {
        if (ctl.signal.aborted) return;
        const envelope = res.data;
        const data = (envelope && !Array.isArray(envelope) ? envelope.data : envelope) || [];
        setCatalog(Array.isArray(data) ? (data as NativeToolEntry[]) : []);
      })
      .catch(() => {
        if (!ctl.signal.aborted) setCatalog([]);
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
    return () => ctl.abort();
  }, [provider, model]);

  useEffect(() => {
    if (!pruneInvalidOnLoad || loading) return;
    const allowed = new Set(catalog.map((t) => t.value));
    const filtered = (selected || []).filter((v) => allowed.has(v));
    if (filtered.length !== (selected || []).length) onChange(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy guardrail #1219
  }, [catalog, loading]);

  const iconFor = (v: string) =>
    v === 'web_search'
      ? '🔍'
      : v === 'code_interpreter'
      ? '💻'
      : v === 'google_search'
      ? '🌐'
      : v === 'code_execution'
      ? '🐍'
      : v === 'url_context'
      ? '🔗'
      : v === 'google_maps'
      ? '🗺️'
      : '⚙️';

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <Label className="text-xs">{label}</Label>
      {description && (
        <p className="text-[10px] text-muted-foreground -mt-1">{description}</p>
      )}
      {loading && catalog.length === 0 ? (
        <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> Caricamento catalogo...
        </div>
      ) : catalog.length === 0 ? (
        <p className="text-[10px] text-muted-foreground">
          Nessun tool nativo disponibile per questo provider.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-1.5">
          {catalog.map((tool) => {
            const cost = tool.cost_hint
              ? `~€${Number(tool.cost_hint.eur).toFixed(4)}/${tool.cost_hint.unit}`
              : null;
            const checked = (selected || []).includes(tool.value);
            return (
              <label
                key={tool.value}
                className="flex items-start gap-1.5 text-xs cursor-pointer p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const current = selected || [];
                    const updated = e.target.checked
                      ? Array.from(new Set([...current, tool.value]))
                      : current.filter((t) => t !== tool.value);
                    onChange(updated);
                  }}
                  className="rounded border-gray-300 mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {iconFor(tool.value)} {tool.label}
                    </span>
                    {cost && (
                      <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                        {cost}
                      </Badge>
                    )}
                  </div>
                  {tool.description && (
                    <p className="text-[10px] text-muted-foreground leading-tight">
                      {tool.description}
                    </p>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
